import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./classSessions.css";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Session interface - updated to include batch info
interface Session {
    id: number;
    session_datetime: string;
    session_type: string;
    duration: number;
    actual_faculty_id: string | null;
    faculty_name: string;
    course_name: string | null;
    section_id: number | null;
    venue?: string;
    batch_info?: {
        batch_id: number;
        batch_name: string;
    } | null;
}

// Faculty interface
interface Faculty {
    id: string;
    name: string;
}

// Section interface
interface Section {
    id: number;
    course_name: string;
}

// Batch interface
interface Batch {
    id: number;
    batch_name: string;
}

// Allowed session types
const SESSION_TYPES = ["theory", "practical", "tutorial", "evaluation", "other"];

const ClassSessions: React.FC = () => {
    const navigate = useNavigate();

    const [date, setDate] = useState<string>("");
    const [batchId, setBatchId] = useState<string>("1"); // Default to batch 1
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    const [editSession, setEditSession] = useState<Session | null>(null);

    const [faculties, setFaculties] = useState<Faculty[]>([]);
    const [sections, setSections] = useState<Section[]>([]);
    const [batches, setBatches] = useState<Batch[]>([]);

    // form states
    const [facultyId, setFacultyId] = useState<string>("");
    const [sectionId, setSectionId] = useState<number | "">("");
    const [dateVal, setDateVal] = useState("");
    const [timeVal, setTimeVal] = useState("");
    const [durationVal, setDurationVal] = useState(60);
    const [typeVal, setTypeVal] = useState("theory");
    const [venueVal, setVenueVal] = useState("");

    const [showQuizModal, setShowQuizModal] = useState(false);
    const [quizSessionId, setQuizSessionId] = useState<number | null>(null);

    const [questions, setQuestions] = useState<any[]>([]);
    const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
    const [newQuestion, setNewQuestion] = useState("");
    const [newQuestionType, setNewQuestionType] = useState("long_text");
    const [newOptions, setNewOptions] = useState<string[]>([""]);

    useEffect(() => {
        // Fetch faculties, sections, and batches once
        axios.get(`${API_BASE_URL}/api/schedule/classSessions/getFaculty`)
            .then(res => setFaculties(res.data))
            .catch(err => console.error("Error fetching faculties:", err));

        axios.get(`${API_BASE_URL}/api/schedule/classCourses`)
            .then(res => setSections(res.data))
            .catch(err => console.error("Error fetching sections:", err));

        // Fetch batches using existing endpoint
        axios.get(`${API_BASE_URL}/api/attendance/batches`)
            .then(res => setBatches(res.data))
            .catch(err => console.error("Error fetching batches:", err));
    }, []);

    const openQuizModal = async (sessionId: number) => {
        setQuizSessionId(sessionId);
        setShowQuizModal(true);

        try {
            const { data } = await axios.get(`${API_BASE_URL}/api/schedule/feedbackQuestions`);
            setQuestions(data.questions || []);
        } catch (err) {
            console.error("Error fetching questions:", err);
        }
    };

    const fetchSessions = async () => {
        if (!date) return;
        try {
            setLoading(true);
            // Updated API call to include batch_id as query parameter
            const res = await axios.get<Session[]>(
                `${API_BASE_URL}/api/schedule/classSessions/getSessions/${date}?batch_id=${batchId}`
            );
            setSessions(res.data);
            setError("");
        } catch {
            setError("Failed to load sessions");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        if (!editSession) return;
        try {
            const istDateTime = `${dateVal}T${timeVal}:00+05:30`;

            await axios.patch(`${API_BASE_URL}/api/schedule/classSessions/${editSession.id}`, {
                actual_faculty_id: facultyId,
                section_id: sectionId,
                session_datetime: istDateTime,
                duration: durationVal,
                session_type: typeVal,
                venue: venueVal
            });

            alert("✅ Session updated successfully");
            setEditSession(null);
            fetchSessions();
        } catch {
            alert("❌ Failed to update session");
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Are you sure you want to delete this session?")) return;

        try {
            await axios.delete(`${API_BASE_URL}/api/schedule/classSessionsdelete/${id}`);
            alert("🗑️ Session deleted successfully");
            fetchSessions();
        } catch (err) {
            console.error("Error deleting session:", err);
            alert("❌ Failed to delete session");
        }
    };

    const openEditModal = (s: Session) => {
        setEditSession(s);
        setFacultyId(s.actual_faculty_id || "");
        setSectionId(s.section_id || "");
        const dt = new Date(s.session_datetime);
        setDateVal(dt.toISOString().slice(0, 10));
        setTimeVal(dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }));
        setDurationVal(s.duration);
        // Normalize session type to lowercase to match dropdown values
        setTypeVal(s.session_type.toLowerCase());
        setVenueVal(s.venue || "");
    };

    return (
        <div className="sessions-container">
            <h1>Class Sessions</h1>
            <button onClick={() => navigate("/sessions/add")}>+ Add Session</button>

            <div className="filter-section">
                <div className="batch-picker">
                    <label>Select Batch:</label>
                    <select
                        value={batchId}
                        onChange={(e) => setBatchId(e.target.value)}
                        style={{ marginRight: "10px" }}
                    >
                        {batches.map((batch) => (
                            <option key={batch.id} value={batch.id}>
                                {batch.batch_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="date-picker">
                    <label>Select Date:</label>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        style={{ marginRight: "10px" }}
                    />
                    <button onClick={fetchSessions}>Fetch Sessions</button>
                </div>
            </div>

            {loading && <p>Loading sessions...</p>}
            {error && <p className="error">{error}</p>}

            <table className="sessions-table">
                <thead>
                    <tr>
                        <th>Date & Time</th>
                        <th>Type</th>
                        <th>Duration</th>
                        <th>Faculty</th>
                        <th>Course</th>
                        <th>Section</th>
                        <th>Batch</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sessions.map((s) => (
                        <tr key={s.id}>
                            <td>{new Date(s.session_datetime).toLocaleString()}</td>
                            <td>{s.session_type}</td>
                            <td>{s.duration} min</td>
                            <td>{s.faculty_name}</td>
                            <td>{s.course_name || "-"}</td>
                            <td>{s.section_id || "-"}</td>
                            <td>{s.batch_info?.batch_name || "-"}</td>
                            <td>
                                <button onClick={() => openEditModal(s)}>✏️ Edit</button>
                                <button
                                    onClick={() => handleDelete(s.id)}
                                    className="delete-btn"
                                    style={{ marginLeft: "8px", color: "white", background: "red" }}
                                >
                                    🗑️ Delete
                                </button>
                                <button
                                    onClick={() => navigate(`/quiz/${s.id}`)}
                                    className="quiz-btn"
                                    style={{ marginLeft: "8px", color: "black", background: "lightpink" }}
                                >
                                    ➕ Add Quiz
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {editSession && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Edit Session #{editSession.id}</h3>

                        <label>Faculty</label>
                        <select value={facultyId} onChange={(e) => setFacultyId(e.target.value)}>
                            <option value="">--Select--</option>
                            {faculties.map((f) => (
                                <option key={f.id} value={f.id}>{f.name}</option>
                            ))}
                        </select>

                        <label>Section</label>
                        <select value={sectionId} onChange={(e) => setSectionId(e.target.value ? Number(e.target.value) : "")}>
                            <option value="">--Select--</option>
                            {sections.map((s) => (
                                <option key={s.id} value={s.id}>{s.course_name}</option>
                            ))}
                        </select>

                        <label>Date</label>
                        <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} />

                        <label>Time</label>
                        <input type="time" value={timeVal} onChange={(e) => setTimeVal(e.target.value)} />

                        <label>Duration (min)</label>
                        <input type="number" value={durationVal} onChange={(e) => setDurationVal(Number(e.target.value))} />

                        <label>Venue</label>
                        <input value={venueVal} onChange={(e) => setVenueVal(e.target.value)} />

                        <label>Type</label>
                        <select value={typeVal} onChange={(e) => setTypeVal(e.target.value)}>
                            {SESSION_TYPES.map((type) => (
                                <option key={type} value={type}>
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                </option>
                            ))}
                        </select>

                        <div className="modal-actions">
                            <button className="save-btn" onClick={handleUpdate}>Save</button>
                            <button className="cancel-btn" onClick={() => setEditSession(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassSessions;